import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { LeaveAuditTab } from '@/components/leave/leave-audit-tab'
import { AbilityContext } from '@/components/providers/ability-provider'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useTRPC } from '@/trpc/react'
import { Can, useAbility } from '@casl/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Info, Pencil, Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

// Label + info icon with a mouseover tooltip explaining what the field controls.
function FieldLabel({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <Label>{label}</Label>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 cursor-help text-muted-foreground" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
      </Tooltip>
    </div>
  )
}

export const Route = createFileRoute('/dashboard/admin/business_settings/leave_types/')({
  component: RouteComponent,
})

type AccrualMethod = 'upfront' | 'periodic'

interface LeaveTypeFormState {
  name: string
  isPaid: boolean
  accrualMethod: AccrualMethod
  accrualRate: number
  prorateForNewHires: boolean
  carryOverAllowed: boolean
  carryOverCapDays: number | null
  cashConvertible: boolean
  negativeBalanceAllowed: boolean
  negativeBalanceCapDays: number | null
  minServiceMonths: number | null
  statutoryMinimumDays: number | null
  halfDayAllowed: boolean
  minNoticeDays: number | null
  requiresAttachment: boolean
}

const EMPTY_LEAVE_TYPE_FORM: LeaveTypeFormState = {
  name: '',
  isPaid: true,
  accrualMethod: 'upfront',
  accrualRate: 0,
  prorateForNewHires: false,
  carryOverAllowed: false,
  carryOverCapDays: null,
  cashConvertible: false,
  negativeBalanceAllowed: false,
  negativeBalanceCapDays: null,
  minServiceMonths: null,
  statutoryMinimumDays: null,
  halfDayAllowed: false,
  minNoticeDays: null,
  requiresAttachment: false,
}

function numberOrNull(value: string): number | null {
  return value === '' ? null : Number(value)
}

// No visible "code" field in this form (Step A3 never had one, and nothing reads it
// yet) — derive a stable short slug from the name instead of asking for one.
function deriveCode(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()
  const ability = useAbility(AbilityContext)

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingLeaveTypeId, setEditingLeaveTypeId] = useState<string | null>(null)
  const [form, setForm] = useState<LeaveTypeFormState>(EMPTY_LEAVE_TYPE_FORM)
  const [activeTab, setActiveTab] = useState('details')

  const { data: leaveTypes, isPending: isPendingLeaveTypes } = useQuery({
    ...api.leave.getLeaveTypesBusiness.queryOptions(),
  })

  const invalidateLeaveTypes = () =>
    queryClient.invalidateQueries({ queryKey: api.leave.getLeaveTypesBusiness.queryKey() })

  const createMutation = useMutation({
    ...api.leave.createLeaveType.mutationOptions({
      onSuccess: () => {
        toast.success('Leave type created')
        invalidateLeaveTypes()
        setIsDialogOpen(false)
      },
      onError: (error) => {
        toast.error('Failed to create leave type', { description: error.message })
      },
    }),
  })

  const updateMutation = useMutation({
    ...api.leave.updateLeaveType.mutationOptions({
      onSuccess: () => {
        toast.success('Leave type updated')
        invalidateLeaveTypes()
        setIsDialogOpen(false)
      },
      onError: (error) => {
        toast.error('Failed to update leave type', { description: error.message })
      },
    }),
  })

  const isSaving = createMutation.isPending || updateMutation.isPending

  function openCreateDialog() {
    setEditingLeaveTypeId(null)
    setForm(EMPTY_LEAVE_TYPE_FORM)
    setActiveTab('details')
    setIsDialogOpen(true)
  }

  function openEditDialog(leaveType: NonNullable<typeof leaveTypes>[number]) {
    setEditingLeaveTypeId(leaveType.leaveTypeId)
    setActiveTab('details')
    setForm({
      name: leaveType.name,
      isPaid: leaveType.isPaid,
      accrualMethod: leaveType.accrualMethod,
      accrualRate: Number(leaveType.accrualRate),
      prorateForNewHires: leaveType.prorateForNewHires,
      carryOverAllowed: leaveType.carryOverAllowed,
      carryOverCapDays:
        leaveType.carryOverCapDays === null ? null : Number(leaveType.carryOverCapDays),
      cashConvertible: leaveType.cashConvertible,
      negativeBalanceAllowed: leaveType.negativeBalanceAllowed,
      negativeBalanceCapDays:
        leaveType.negativeBalanceCapDays === null
          ? null
          : Number(leaveType.negativeBalanceCapDays),
      minServiceMonths: leaveType.minServiceMonths,
      statutoryMinimumDays:
        leaveType.statutoryMinimumDays === null ? null : Number(leaveType.statutoryMinimumDays),
      halfDayAllowed: leaveType.halfDayAllowed,
      minNoticeDays: leaveType.minNoticeDays,
      requiresAttachment: leaveType.requiresAttachment,
    })
    setIsDialogOpen(true)
  }

  function handleSubmit() {
    if (form.name.trim() === '') return

    const sharedFields = {
      name: form.name,
      isPaid: form.isPaid,
      accrualMethod: form.accrualMethod,
      accrualRate: String(form.accrualRate),
      prorateForNewHires: form.prorateForNewHires,
      carryOverAllowed: form.carryOverAllowed,
      carryOverCapDays: form.carryOverCapDays === null ? null : String(form.carryOverCapDays),
      cashConvertible: form.cashConvertible,
      negativeBalanceAllowed: form.negativeBalanceAllowed,
      negativeBalanceCapDays:
        form.negativeBalanceCapDays === null ? null : String(form.negativeBalanceCapDays),
      minServiceMonths: form.minServiceMonths,
      statutoryMinimumDays:
        form.statutoryMinimumDays === null ? null : String(form.statutoryMinimumDays),
      halfDayAllowed: form.halfDayAllowed,
      minNoticeDays: form.minNoticeDays,
      requiresAttachment: form.requiresAttachment,
    }

    if (editingLeaveTypeId) {
      updateMutation.mutate({ leaveTypeId: editingLeaveTypeId, ...sharedFields })
    } else {
      createMutation.mutate({ ...sharedFields, code: deriveCode(form.name) })
    }
  }

  return (
    <DashboardShell>
      <DashboardHeader
        heading="Leave Types"
        text="Define the leave types available to employees at this business."
      >
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          New Leave Type
        </Button>
      </DashboardHeader>

      <Card className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
        <CardContent className="pt-6">
          {isPendingLeaveTypes ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead>Accrual</TableHead>
                  <TableHead>Half-day</TableHead>
                  <TableHead>Cash-convertible</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(leaveTypes ?? []).map((leaveType) => (
                  <TableRow key={leaveType.leaveTypeId}>
                    <TableCell className="font-medium">{leaveType.name}</TableCell>
                    <TableCell>
                      <Badge variant={leaveType.isPaid ? 'default' : 'secondary'}>
                        {leaveType.isPaid ? 'Paid' : 'Unpaid'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {leaveType.accrualRate} days ({leaveType.accrualMethod})
                    </TableCell>
                    <TableCell>{leaveType.halfDayAllowed ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{leaveType.cashConvertible ? 'Yes' : 'No'}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(leaveType)}
                      >
                        <Pencil className="mr-1 h-3.5 w-3.5" />
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-h-[90vh] sm:max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLeaveTypeId ? 'Edit Leave Type' : 'New Leave Type'}</DialogTitle>
            <DialogDescription>
              Configure how this leave type accrues, carries over, and is requested.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {editingLeaveTypeId && (
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Details</TabsTrigger>
                <Can I="read" a="LeaveAuditTab" ability={ability}>
                  <TabsTrigger value="audit">Audit Trail</TabsTrigger>
                </Can>
              </TabsList>
            )}

            <TabsContent value="details">
              <TooltipProvider>
                <div className="grid gap-4 py-2">
                  <div className="grid gap-2">
                    <FieldLabel
                      label="Name"
                      tooltip="The name shown to employees and approvers when selecting this leave type."
                    />
                    <Input
                      placeholder="e.g. Vacation Leave"
                      value={form.name}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, name: event.target.value }))
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <FieldLabel
                      label="Paid"
                      tooltip="Whether this leave type continues paying the employee. If off, monthly employees get an absence deduction and daily/hourly employees simply aren't paid for the day."
                    />
                    <Switch
                      checked={form.isPaid}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({ ...prev, isPaid: checked }))
                      }
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <FieldLabel
                        label="Accrual method"
                        tooltip="Upfront grants the full entitlement at once (e.g. at year start or on hire). Periodic would accrue it gradually over time — not yet supported, since it needs a recurring scheduler this app doesn't have."
                      />
                      <Select
                        value={form.accrualMethod}
                        onValueChange={(value: AccrualMethod) =>
                          setForm((prev) => ({ ...prev, accrualMethod: value }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="upfront">Upfront (grant all at once)</SelectItem>
                          {/* Periodic accrual needs a recurring scheduler this codebase doesn't have yet
                        (see "Periodic accrual" in the plan's Deferred to Future Phases section) —
                        locked out of the v1 UI rather than silently doing nothing if selected. */}
                          <SelectItem value="periodic" disabled>
                            Periodic (coming soon)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <FieldLabel
                        label="Accrual rate (days)"
                        tooltip="The number of days granted per year (or per accrual period, once periodic accrual is supported)."
                      />
                      <Input
                        type="number"
                        step="0.5"
                        value={form.accrualRate}
                        onChange={(event) =>
                          setForm((prev) => ({ ...prev, accrualRate: Number(event.target.value) }))
                        }
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <FieldLabel
                      label="Prorate for new hires"
                      tooltip="If on, an employee hired partway through the year gets a reduced entitlement proportional to their remaining time in the year, instead of the full amount."
                    />
                    <Switch
                      checked={form.prorateForNewHires}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({ ...prev, prorateForNewHires: checked }))
                      }
                    />
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <FieldLabel
                      label="Carry-over allowed"
                      tooltip="Whether unused balance rolls over into the next entitlement period instead of being forfeited."
                    />
                    <Switch
                      checked={form.carryOverAllowed}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({
                          ...prev,
                          carryOverAllowed: checked,
                          carryOverCapDays: checked ? prev.carryOverCapDays : null,
                        }))
                      }
                    />
                  </div>
                  {form.carryOverAllowed && (
                    <div className="grid gap-2">
                      <FieldLabel
                        label="Carry-over cap (days)"
                        tooltip="The maximum number of unused days that can roll over; anything above this is forfeited."
                      />
                      <Input
                        type="number"
                        value={form.carryOverCapDays ?? ''}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            carryOverCapDays: numberOrNull(event.target.value),
                          }))
                        }
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <FieldLabel
                      label="Cash-convertible"
                      tooltip="Whether unused balance can be paid out in cash (encashed) instead of only being used as time off — typically triggered manually on resignation."
                    />
                    <Switch
                      checked={form.cashConvertible}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({ ...prev, cashConvertible: checked }))
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <FieldLabel
                      label="Negative balance allowed"
                      tooltip="Whether an employee can request more days than they currently have, taking their balance negative."
                    />
                    <Switch
                      checked={form.negativeBalanceAllowed}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({
                          ...prev,
                          negativeBalanceAllowed: checked,
                          negativeBalanceCapDays: checked ? prev.negativeBalanceCapDays : null,
                        }))
                      }
                    />
                  </div>
                  {form.negativeBalanceAllowed && (
                    <div className="grid gap-2">
                      <FieldLabel
                        label="Negative balance cap (days)"
                        tooltip="The maximum negative balance allowed before further requests of this type are blocked."
                      />
                      <Input
                        type="number"
                        value={form.negativeBalanceCapDays ?? ''}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            negativeBalanceCapDays: numberOrNull(event.target.value),
                          }))
                        }
                      />
                    </div>
                  )}

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <FieldLabel
                        label="Min. service (months)"
                        tooltip="How many months of employment are required before an employee is eligible to use this leave type."
                      />
                      <Input
                        type="number"
                        placeholder="None"
                        value={form.minServiceMonths ?? ''}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            minServiceMonths: numberOrNull(event.target.value),
                          }))
                        }
                      />
                    </div>
                    <div className="grid gap-2">
                      <FieldLabel
                        label="Statutory minimum (days)"
                        tooltip="The legally required minimum for this leave type, if any. Shown for reference only — not automatically enforced."
                      />
                      <Input
                        type="number"
                        placeholder="Informational only"
                        value={form.statutoryMinimumDays ?? ''}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            statutoryMinimumDays: numberOrNull(event.target.value),
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                      <FieldLabel
                        label="Half-day allowed"
                        tooltip="Whether employees can request half a day of this leave type instead of only full days."
                      />
                      <Switch
                        checked={form.halfDayAllowed}
                        onCheckedChange={(checked) =>
                          setForm((prev) => ({ ...prev, halfDayAllowed: checked }))
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                      <FieldLabel
                        label="Requires attachment"
                        tooltip="Whether an employee must upload a supporting document (e.g. a medical certificate) to submit this type of request."
                      />
                      <Switch
                        checked={form.requiresAttachment}
                        onCheckedChange={(checked) =>
                          setForm((prev) => ({ ...prev, requiresAttachment: checked }))
                        }
                      />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <FieldLabel
                      label="Min. notice (days)"
                      tooltip="How many days in advance a request must be submitted before the leave's start date."
                    />
                    <Input
                      type="number"
                      placeholder="None"
                      value={form.minNoticeDays ?? ''}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          minNoticeDays: numberOrNull(event.target.value),
                        }))
                      }
                    />
                  </div>
                </div>
              </TooltipProvider>
            </TabsContent>

            {editingLeaveTypeId && (
              <Can I="read" a="LeaveAuditTab" ability={ability}>
                <TabsContent value="audit">
                  <LeaveAuditTab
                    resourceType="LeaveType"
                    resourceId={editingLeaveTypeId}
                    enabled={activeTab === 'audit'}
                  />
                </TabsContent>
              </Can>
            )}
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button disabled={form.name.trim() === '' || isSaving} onClick={handleSubmit}>
              {isSaving ? 'Saving...' : editingLeaveTypeId ? 'Save Changes' : 'Create Leave Type'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  )
}
